import React, { useEffect, useState } from 'react';
import { LayoutDashboard, Users, Columns3, CheckSquare, Settings, LogOut, Bell, BellOff, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '../../contexts/AuthContext';
import {
  isPushSupported,
  getPushPermission,
  requestPushPermission,
  subscribeToPush,
  unsubscribeFromPush,
  getCurrentPushSubscription,
} from '../../lib/pushNotifications';

export type AppTab = 'dashboard' | 'leads' | 'pipeline' | 'tasks' | 'setup';

interface NavItem {
  id: AppTab;
  label: string;
  icon: React.ReactNode;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard className="w-5 h-5" /> },
  { id: 'leads', label: 'Anfragen', icon: <Users className="w-5 h-5" /> },
  { id: 'pipeline', label: 'Pipeline', icon: <Columns3 className="w-5 h-5" /> },
  { id: 'tasks', label: 'Tasks', icon: <CheckSquare className="w-5 h-5" /> },
  { id: 'setup', label: 'Setup', icon: <Settings className="w-5 h-5" /> },
];

interface AppShellProps {
  activeTab: AppTab;
  onTabChange: (tab: AppTab) => void;
  onLogout?: () => void;
  userName: string;
  children: React.ReactNode;
  impersonationBanner?: React.ReactNode;
}

const AppShell: React.FC<AppShellProps> = ({ activeTab, onTabChange, onLogout, userName, children, impersonationBanner }) => {
  const { isKunde, isAdmin, isImpersonating, currentUser } = useAuth();
  const adminWithoutImpersonation = isAdmin() && !isImpersonating();
  const visibleNavItems = adminWithoutImpersonation
    ? []
    : isKunde()
      ? NAV_ITEMS.filter(item => item.id !== 'tasks' && item.id !== 'setup')
      : NAV_ITEMS;

  const [showPushModal, setShowPushModal] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    getCurrentPushSubscription().then(sub => setPushSubscribed(!!sub));
  }, []);

  const handlePushToggle = async () => {
    if (!currentUser) return;
    setPushLoading(true);
    if (pushSubscribed) {
      await unsubscribeFromPush(currentUser.id);
      setPushSubscribed(false);
    } else {
      const permission = getPushPermission();
      if (permission !== 'granted') {
        const result = await requestPushPermission();
        if (result !== 'granted') { setPushLoading(false); return; }
      }
      const ok = await subscribeToPush(currentUser.id);
      setPushSubscribed(ok);
    }
    setPushLoading(false);
    setShowPushModal(false);
  };

  return (
    <div className="flex min-h-screen bg-zinc-50">
      {/* Sidebar – Desktop */}
      <aside className="hidden md:flex flex-col w-60 min-h-screen bg-zinc-900 border-r border-zinc-800 fixed left-0 top-0 z-20">
        {/* Logo */}
        <div className="px-6 py-6 border-b border-zinc-800 flex items-start justify-between">
          <div>
            <h1 className="text-white font-black text-xl tracking-widest uppercase leading-tight">Webzen<br />Board</h1>
            <p className="text-zinc-500 text-xs mt-1 tracking-wide">{userName}</p>
          </div>
          {isPushSupported() && (
            <button
              onClick={() => setShowPushModal(true)}
              className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
              title="Benachrichtigungen"
            >
              {pushSubscribed ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
            </button>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          {visibleNavItems.map((item) => {
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onTabChange(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-white text-zinc-900'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
              >
                {item.icon}
                <span className="tracking-wide">{item.label}</span>
                {isActive && (
                  <motion.div
                    layoutId="sidebar-indicator"
                    className="ml-auto w-1.5 h-1.5 rounded-full bg-zinc-900"
                  />
                )}
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="px-4 py-4 border-t border-zinc-800 space-y-3">
          <p className="text-zinc-600 text-xs px-2">© 2026 Webzen Studio - All rights reserved.</p>
          {onLogout && (
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-400 hover:text-white hover:bg-zinc-800 transition-all"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0 md:ml-60 flex flex-col min-h-screen overflow-x-hidden">
        {impersonationBanner}

        {/* Page content */}
        <main className="flex-1 pb-20 md:pb-0">
          {children}
        </main>
      </div>

      {/* Bottom Nav – Mobile */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-20 bg-zinc-900 border-t border-zinc-800 flex">
        {visibleNavItems.map((item) => {
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className={`flex-1 flex flex-col items-center justify-center py-3 gap-1 transition-colors min-h-[56px] ${
                isActive ? 'text-white' : 'text-zinc-500'
              }`}
            >
              {item.icon}
              <span className="text-[10px] font-medium tracking-wide">{item.label}</span>
              {isActive && (
                <motion.div
                  layoutId="bottom-nav-indicator"
                  className="absolute bottom-1 w-5 h-0.5 bg-white rounded-full"
                />
              )}
            </button>
          );
        })}
        {isPushSupported() && (
          <button
            onClick={() => setShowPushModal(true)}
            className="flex-1 flex flex-col items-center justify-center py-3 gap-1 transition-colors min-h-[56px] text-zinc-500 hover:text-white"
          >
            {pushSubscribed ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
            <span className="text-[10px] font-medium tracking-wide">Alerts</span>
          </button>
        )}
        {onLogout && (
          <button
            onClick={onLogout}
            className="flex-1 flex flex-col items-center justify-center py-3 gap-1 transition-colors min-h-[56px] text-zinc-500 hover:text-white"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-[10px] font-medium tracking-wide">Logout</span>
          </button>
        )}
      </nav>

      {/* Push Notification Modal */}
      <AnimatePresence>
        {showPushModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4 bg-black/40"
            onClick={() => setShowPushModal(false)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-zinc-900 uppercase tracking-widest">Benachrichtigungen</h3>
                <button onClick={() => setShowPushModal(false)} className="p-1 text-zinc-400 hover:text-zinc-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm text-zinc-500 mb-5">
                {pushSubscribed
                  ? 'Du bekommst Benachrichtigungen bei neuen Anfragen.'
                  : 'Aktiviere Benachrichtigungen um bei neuen Anfragen sofort informiert zu werden.'}
              </p>
              <button
                onClick={handlePushToggle}
                disabled={pushLoading}
                className={`w-full py-3 rounded-xl text-sm font-semibold transition-colors ${
                  pushSubscribed
                    ? 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                    : 'bg-zinc-900 text-white hover:bg-zinc-800'
                } disabled:opacity-50`}
              >
                {pushLoading ? 'Bitte warten...' : pushSubscribed ? 'Deaktivieren' : 'Aktivieren'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AppShell;
